/** Browser cache for crop calendar results (same farm + crop details, same day). */

const STORAGE_PREFIX = 'digisaka.cropCalendar.v1:';
const TTL_MS = 6 * 60 * 60 * 1000;
const memory = new Map<string, { expiresAt: number; data: unknown }>();

export type CropCalendarCacheRequest = {
  farm_id: string;
  establishment_date: string;
  crop_variety: string;
  planting_method?: string;
  sowing_date?: string;
  planting_date?: string;
  expected_harvest_date?: string;
  cropping_season?: string;
};

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function norm(value: string | undefined): string {
  return String(value || '').trim().toLowerCase();
}

export function cropCalendarCacheKey(req: CropCalendarCacheRequest): string {
  return JSON.stringify({
    farm_id: String(req.farm_id || '').trim(),
    establishment_date: String(req.establishment_date || '').trim(),
    crop_variety: norm(req.crop_variety),
    planting_method: norm(req.planting_method),
    sowing_date: String(req.sowing_date || '').trim(),
    planting_date: String(req.planting_date || '').trim(),
    expected_harvest_date: String(req.expected_harvest_date || '').trim(),
    cropping_season: norm(req.cropping_season),
    day: todayIso(),
  });
}

function storageKey(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return `${STORAGE_PREFIX}${Math.abs(h).toString(36)}`;
}

function isUsable(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const row = data as { success?: boolean; llm_error?: unknown; llm_success?: boolean; ndvi_series?: unknown[] };
  if (!row.success || row.llm_error) return false;
  return Boolean(row.llm_success) || (Array.isArray(row.ndvi_series) && row.ndvi_series.length > 0);
}

export function readCropCalendarCache(req: CropCalendarCacheRequest): any | null {
  const key = cropCalendarCacheKey(req);
  const now = Date.now();
  const mem = memory.get(key);
  if (mem && mem.expiresAt > now && isUsable(mem.data)) return mem.data;

  try {
    const raw = sessionStorage.getItem(storageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { expiresAt?: number; data?: unknown };
    if (!parsed?.expiresAt || parsed.expiresAt <= now || !isUsable(parsed.data)) {
      sessionStorage.removeItem(storageKey(key));
      return null;
    }
    memory.set(key, { expiresAt: parsed.expiresAt, data: parsed.data });
    return parsed.data;
  } catch {
    return null;
  }
}

export function writeCropCalendarCache(req: CropCalendarCacheRequest, data: unknown): void {
  if (!isUsable(data)) return;
  const key = cropCalendarCacheKey(req);
  const entry = { expiresAt: Date.now() + TTL_MS, data };
  memory.set(key, entry);
  try {
    sessionStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    /* quota / private mode — memory cache still works for this tab */
  }
}
